import React, { FormEvent, useState } from 'react';

import {
  Box,
  Button,
  Checkbox,
  Divider,
  Group,
  Input,
  LoadingOverlay,
  Select,
  Stack,
  Switch,
  Text,
  TextInput,
  Textarea,
  Title,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { useShallowEffect } from '@mantine/hooks';
import { IconCalendar, IconFileDescription, IconHome, IconMail, IconTags } from '@tabler/icons-react';
import Drawer from 'components/Drawer';
import AthleteAutoComplete from 'components/form-fields/auto-complete-fields/AthleteAutoComplete';
import RegionSelect from 'components/form-fields/auto-complete-fields/RegionSelect';
import PostalCodeInput from 'components/form-fields/input-fields/PostalCodeInput';
import CountrySelect from 'components/form-fields/select-fields/CountrySelect';
import HelpButton from 'components/HelpButton';
import KiloAlert from 'components/KiloAlert';
import { useAuth } from 'context/auth-context';
import { useLocalization } from 'context/localization-context';
import dayjs from 'dayjs';
import useCreateConnectedAthlete from 'hooks/athlete/connected/useCreateConnectedAthlete';
import useIndependentToLinked from 'hooks/athlete/connected/useIndependentToLinked';
import useAthlete from 'hooks/athlete/useAthlete';
import useCreateAthlete from 'hooks/athlete/useCreateAthlete';
import useUpdateAthlete from 'hooks/athlete/useUpdateAthlete';
import useGym from 'hooks/gym/useGym';
import { useStaffTracking } from 'hooks/mixpanel/useStaffTracking';
import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import { Country, CountryEnum } from 'types/enums';
import { getChangedKeysForAthleteEdit } from 'utils/analytics-selectors';
import { formatDateForApi, parseDateTimeToDate } from 'utils/date-functions';
import {
  emailValidator,
  nameValidator,
  phoneValidator,
  optionalDateFormatValidator,
  optionalPastDateValidator,
  optionalMinYearValidator,
} from 'utils/form-validators';
import { formatPhone, phoneInputCountries } from 'utils/formatters';
import { generateDemoGymFakeEmail } from 'utils/functions';
import notificationHandler from 'utils/notifications';
import { v4 as uuidv4 } from 'uuid';

interface FormValues {
  athleteId: string;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string | null;
  dob?: Date | null;
  gender?:
    | 'Male'
    | 'Female'
    | 'Transgender'
    | 'Non-binary/Non-conforming'
    | 'Prefer not to respond'
    | ''
    | null;
  address: {
    addressLines: string | null;
    locality: string | null;
    region: string | null;
    zipCode: string | null;
    country: Country;
  };
  medicalNotes?: string | null;
  emergencyName?: string | null;
  emergencyPhone?: string | null;
  memberSince?: Date | null;
  disableSmsNotifications: boolean | null;
}

interface IDrawerAthleteFormProps {
  type: 'create' | 'edit' | 'create-dependent' | 'create-linked' | 'closed';
  opened: boolean;
  closeHandler: () => void;
  athleteId?: string;
  primaryAthleteId?: string;
}

const DrawerAthleteForm = ({
  type,
  opened,
  closeHandler,
  athleteId,
  primaryAthleteId,
}: IDrawerAthleteFormProps): JSX.Element => {
  const { session } = useAuth();
  const { dateFormat } = useLocalization();

  const isDemoGym = !!session?.demoGym;
  const gymData = useGym(session?.gym?.gymId);
  const staffTracking = useStaffTracking();

  const { data, isLoading, isSuccess } = useAthlete(athleteId);
  const { data: primaryAthleteData, isSuccess: isPrimaryAthleteSucess } = useAthlete(primaryAthleteId);

  const [isExistingAthlete, setIsExistingAthlete] = useState<boolean>(false);
  const [existingAthlete, setExistingAthlete] = useState<{
    athleteId: string;
    label: string;
    value: string;
  }>({
    athleteId: '',
    label: '',
    value: '',
  });

  const canChangePassword = data?.data.canChangeEmail || false;

  const { successNotification, errorNotification } = notificationHandler();

  const createMutation = useCreateAthlete();
  const updateMutation = useUpdateAthlete();
  const createConnectedAthlete = useCreateConnectedAthlete();
  const linkAthlete = useIndependentToLinked();

  const parseDateInput = (input: string): Date | null => {
    const digits = input.replace(/[^\d]/g, '');
    if (digits.length !== 8) return null;

    const normalized = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
    const d = dayjs(normalized, dateFormat, true);
    return d.isValid() ? d.toDate() : null;
  };

  const form = useForm<FormValues>({
    validateInputOnBlur: true,
    initialValues: {
      athleteId: '',
      email: isDemoGym && type !== 'edit' ? generateDemoGymFakeEmail() : '',
      firstName: '',
      lastName: '',
      phone: '',
      dob: null,
      gender: null,
      address: {
        addressLines: data?.data?.address?.addressLines || '',
        locality: data?.data?.address?.locality || '',
        region: data?.data?.address?.region || '',
        zipCode: data?.data?.address?.zipCode || '',
        country: data?.data?.address?.country || session?.gym?.country || CountryEnum.USA,
      },
      medicalNotes: '',
      emergencyName: '',
      emergencyPhone: '',
      memberSince: null,
      disableSmsNotifications: false,
    },
    validate: {
      firstName: value => nameValidator(value),
      lastName: value => nameValidator(value),
      email: value => emailValidator(value),
      dob: value =>
        optionalDateFormatValidator(value, dateFormat) ??
        optionalPastDateValidator(value, 'Date of birth') ??
        optionalMinYearValidator(value, 1900),
      memberSince: value =>
        optionalDateFormatValidator(value, dateFormat) ??
        optionalPastDateValidator(value, 'Member since') ??
        optionalMinYearValidator(value, 1900),
      phone: value => (value ? phoneValidator(value) : null),
      emergencyName: value =>
        value && value.length > 128 ? 'Emergency name must be between 1-128 characters' : null,
      emergencyPhone: value => (value ? phoneValidator(value) : null),
    },

    transformValues: values => ({
      ...values,
      phone: formatPhone(values.phone),
      emergencyPhone: formatPhone(values.emergencyPhone),
      address: {
        ...values.address,
        zipCode: values.address.zipCode?.toUpperCase() || null,
      },
    }),
  });

  const handleCountryChange = (value: string): void => {
    if (form.values.address.country != value) {
      form.setFieldValue('address.zipCode', '');
      form.setFieldValue('address.region', '');
      form.setFieldValue('address.country', value);
    }
  };

  const handleCloseAndReset = (): void => {
    closeHandler();
    setIsExistingAthlete(false);
    setExistingAthlete({ athleteId: '', label: '', value: '' });
    form.reset();
  };

  const buildPayload = (values: FormValues): any => {
    return {
      ...values,
      dob: values.dob ? dayjs(values.dob).format('YYYY-MM-DD') : null,
      // ENG-2344: member_since is a calendar date — serialize the picked day directly (the form
      // Date is already the gym-local day) without any browser-timezone conversion.
      memberSince: formatDateForApi(values.memberSince) ?? null,
      phone: values.phone && values.phone.length > 10 ? values.phone : '',
      gender: values.gender ?? null,
      medicalNotes: values.medicalNotes ?? null,
      emergencyName: values.emergencyName ?? null,
      emergencyPhone:
        values.emergencyPhone && values.emergencyPhone.length > 10 ? values.emergencyPhone : '',
      disableSmsNotifications: values.disableSmsNotifications ?? false,
    };
  };

  const handleCreateSubmit = (values: FormValues): void => {
    const payload = buildPayload(values);
    payload.athleteId = uuidv4();
    createMutation.mutate(payload, {
      onError: error => {
        errorNotification('Athlete creation failed', error.response?.data.message);
      },
      onSuccess: response => {
        staffTracking.trackAthleteCreate(
          payload.athleteId,
          response.data.fullName,
          response.data.memberSince,
        );
        successNotification('Athlete creation success', `${response.data.fullName} successfully created.`);
        handleCloseAndReset();
      },
    });
  };

  const handleEditSubmit = (values: FormValues): void => {
    const payload = buildPayload(values);
    let changedKeys = getChangedKeysForAthleteEdit(data?.data, values, session?.gym?.timezone);
    // ENG-867: only send member_since when staff actually changed it. A null (cleared/stale
    // field) must not wipe the stored join date, and re-sending an unchanged value round-trips
    // it through the browser timezone and walks the date back a day. Reuse the same
    // change-detection the analytics use; the backend also preserves on empty as the safety net.
    const memberSinceOmitted = payload.memberSince == null || !changedKeys.includes('member_since');
    if (memberSinceOmitted) {
      delete payload.memberSince;
      // Not persisted, so drop it from the tracked keys — otherwise analytics report a change
      // that never happened.
      changedKeys = changedKeys.filter(key => key !== 'member_since');
    }
    updateMutation.mutate(payload, {
      onError: error => {
        errorNotification('Athlete update failed', error.response?.data.message);
      },
      onSuccess: response => {
        successNotification('Athlete update success', `${response.data.fullName} successfully updated.`);
        if (changedKeys.length > 0 && response.data.athleteId && response.data.fullName) {
          staffTracking.trackAthleteUpdated(response.data.athleteId, response.data.fullName, changedKeys);
        }
        handleCloseAndReset();
      },
    });
  };

  const handleCreateConnectedSubmit = (values: FormValues): void => {
    const payload = buildPayload(values);
    createConnectedAthlete.mutate(
      {
        ...payload,
        athleteId: uuidv4(),
        primaryAccount: primaryAthleteId,
        isDependent: type === 'create-dependent',
      },
      {
        onError: error => {
          errorNotification('Connected athlete creation failed', error.response?.data.message);
        },
        onSuccess: () => {
          successNotification('Connected athlete creation success');
          handleCloseAndReset();
        },
      },
    );
  };

  const handleLinkExistingAthlete = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    if (primaryAthleteId) {
      linkAthlete.mutate(
        {
          athleteId: existingAthlete.athleteId,
          primaryAccount: primaryAthleteId,
        },
        {
          onError: (error: any) => {
            errorNotification('Link athlete failed', error.response?.data.message);
          },
          onSuccess: () => {
            successNotification('Link athlete success');
            handleCloseAndReset();
          },
        },
      );
    }
  };

  const getTitle = (): JSX.Element => {
    if (type === 'create') return <Title order={3}>Create Athlete</Title>;
    if (type === 'edit') return <Title order={3}>Edit Athlete</Title>;
    if (type === 'create-dependent')
      return (
        <Group p={0}>
          <Title order={3}>Create Dependent Athlete</Title>
          <HelpButton width={'lg'} position='bottom-start'>
            <Text size='sm' sx={{ whiteSpace: 'normal' }}>
              A dependent athlete cannot log into their account. They are managed by the primary account.
              This is a good option for a child of a parent at the gym.
            </Text>
          </HelpButton>
        </Group>
      );
    if (type === 'create-linked')
      return (
        <Group p={0}>
          <Title order={3}>Create Linked Athlete</Title>
          <HelpButton width={'lg'} position='bottom-start'>
            <Text size='sm' sx={{ whiteSpace: 'normal' }}>
              A linked athlete manages their own account but shares payment methods with the primary account.
              This is a good option for couples using the same payment method.
            </Text>
          </HelpButton>
        </Group>
      );
    return <></>;
  };

  useShallowEffect(() => {
    if (athleteId && isSuccess && data?.data) {
      form.setValues({
        ...data.data,
        dob: data.data.dob ? dayjs(data.data.dob).toDate() : undefined,
        // ENG-2344: interpret the stored timestamp in the GYM's timezone, not the browser's, so
        // the prefilled date is the gym-local calendar date (mirrors the package date pattern).
        memberSince: data.data.memberSince
          ? parseDateTimeToDate(data.data.memberSince, session?.gym?.timezone)
          : undefined,
        firstName: data.data.firstName,
        lastName: data.data.lastName,
        email: data.data.email,
        gender: data.data.gender,
        phone: data.data.phone,
        disableSmsNotifications: data.data.disableSmsNotifications,
      });
    }
  }, [data?.data, athleteId, opened]);

  useShallowEffect(() => {
    if (type === 'create-dependent' && isPrimaryAthleteSucess && primaryAthleteData?.data) {
      const emailToSet = isDemoGym ? generateDemoGymFakeEmail() : primaryAthleteData.data.email || '';
      form.setFieldValue('email', emailToSet);
    }
  }, [type, isPrimaryAthleteSucess, primaryAthleteData?.data, isDemoGym]);

  // Reset form and regenerate email when drawer opens for demo gyms
  React.useEffect(() => {
    if (opened && isDemoGym && type !== 'edit' && !athleteId) {
      form.setFieldValue('email', generateDemoGymFakeEmail());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, isDemoGym, type, athleteId]);

  return (
    <Drawer
      opened={opened}
      size={'lg'}
      title={getTitle()}
      closeOnClickOutside
      footer={
        <Group position='right'>
          <Button variant='subtle' onClick={handleCloseAndReset} data-cy='cancelButton'>
            Cancel
          </Button>
          <Button
            data-cy='saveAthleteButton'
            size='sm'
            type='submit'
            form={isExistingAthlete ? 'independent-to-linked' : 'athlete-form'}
            loading={
              createMutation.isLoading ||
              updateMutation.isLoading ||
              createConnectedAthlete.isLoading ||
              linkAthlete.isLoading ||
              isLoading
            }
          >
            Save
          </Button>
        </Group>
      }
      onClose={handleCloseAndReset}
    >
      <LoadingOverlay visible={isLoading} />
      {!isExistingAthlete ? (
        <form
          id={'athlete-form'}
          onSubmit={form.onSubmit(
            type === 'create'
              ? handleCreateSubmit
              : type === 'edit'
              ? handleEditSubmit
              : handleCreateConnectedSubmit,
          )}
        >
          <Stack spacing='xs'>
            {type === 'create-dependent' && session?.gym?.hybridafEnabled && (
              <KiloAlert variantStyle='warning' data-cy='dependentHybridAfWarning'>
                Hybrid AF requires a unique email address for each member. This dependent shares the primary
                account holder&apos;s email, so they will not get Hybrid AF access. Create them as a linked
                athlete with their own email address if they need it.
              </KiloAlert>
            )}
            {type === 'create-linked' && (
              <Group grow align='top' data-cy='useExistingAthlete'>
                <Switch
                  label='Use an existing athlete'
                  onClick={() => setIsExistingAthlete(!isExistingAthlete)}
                />
              </Group>
            )}
            <Group grow align='top'>
              <TextInput
                data-cy='athleteFirstNameInput'
                label='First Name'
                placeholder='Enter First Name'
                required
                icon={<IconTags />}
                {...form.getInputProps('firstName')}
              />
              <TextInput
                data-cy='athleteLastNameInput'
                label='Last Name'
                placeholder='Enter Last Name'
                required
                icon={<IconTags />}
                {...form.getInputProps('lastName')}
              />
            </Group>

            <Group>
              <TextInput
                data-cy='athleteEmailInput'
                type='email'
                label='Email / Login'
                placeholder={isDemoGym && type !== 'edit' ? 'Auto-generated demo email' : 'Enter Email'}
                required
                icon={<IconMail />}
                disabled={
                  type === 'create-dependent' || (type === 'edit' && !canChangePassword) || isDemoGym
                }
                sx={{ flexGrow: 1 }}
                {...form.getInputProps('email')}
              />
              {((type === 'edit' && !canChangePassword) || (isDemoGym && type === 'edit')) && (
                <Box pt='20px'>
                  <HelpButton width={'md'} position='bottom-start' withinPortal>
                    <Text size='sm' sx={{ whiteSpace: 'normal' }}>
                      {isDemoGym
                        ? 'Email editing is disabled in demo mode.'
                        : 'This user has activated their account using this email. To maintain login security, the email change must be initiated by the user.'}
                    </Text>
                  </HelpButton>
                </Box>
              )}
            </Group>

            <Group grow align='top'>
              <Input.Wrapper error={form.getInputProps('phone').error} data-cy='athletePhoneNumber'>
                <Input.Label>Phone Number</Input.Label>
                <PhoneInput
                  defaultCountry={session?.gym?.country.toLowerCase()}
                  countries={phoneInputCountries}
                  value={form.values.phone || ''}
                  onChange={form.setFieldValue.bind(null, 'phone')}
                  placeholder='Enter Phone Number'
                  style={{ position: 'relative', zIndex: 2 }}
                  inputStyle={{ width: '100%' }}
                />
              </Input.Wrapper>
              <DateInput
                data-cy='dobInput'
                label='Date of Birth'
                placeholder={dateFormat}
                valueFormat={dateFormat}
                dateParser={parseDateInput}
                clearable
                fixOnBlur
                defaultLevel='decade'
                maxDate={dayjs().toDate()}
                icon={<IconCalendar />}
                {...form.getInputProps('dob')}
              />
            </Group>

            <Group grow align='top'>
              <Select
                data-cy='genderSelectBox'
                label='Gender'
                placeholder='Select Gender'
                icon={<IconTags />}
                data={[
                  'Male',
                  'Female',
                  'Transgender',
                  'Non-binary/Non-conforming',
                  'Prefer not to respond',
                ]}
                {...form.getInputProps('gender')}
              />
              <DateInput
                data-cy='memberSinceInput'
                label='Member Since'
                placeholder={dateFormat}
                valueFormat={dateFormat}
                dateParser={parseDateInput}
                clearable={type !== 'edit'}
                fixOnBlur
                defaultLevel='month'
                maxDate={dayjs().toDate()}
                icon={<IconCalendar />}
                {...form.getInputProps('memberSince')}
              />
            </Group>

            <TextInput
              data-cy='addressInput'
              label='Address'
              placeholder='Enter Address'
              icon={<IconHome />}
              {...form.getInputProps('address.addressLines')}
            />

            <Group grow align='top'>
              <TextInput
                data-cy='cityInput'
                label='City'
                placeholder='Enter City'
                icon={<IconHome />}
                {...form.getInputProps('address.locality')}
              />
              <RegionSelect
                country={form.values.address.country}
                icon={<IconHome />}
                byCountryProps={{
                  [CountryEnum.USA]: { 'data-cy': 'stateDropdown', clearable: true },
                }}
                {...form.getInputProps('address.region')}
              />
            </Group>

            <Group grow align='top'>
              <PostalCodeInput
                country={form.values.address.country}
                data-cy='zipcodeInput'
                icon={<IconHome />}
                {...form.getInputProps('address.zipCode')}
              />
              <CountrySelect
                icon={<IconHome />}
                required
                {...form.getInputProps('address.country')}
                onChange={(value: string) => handleCountryChange(value)}
              />
            </Group>

            <Divider mx='-xl' my='lg' />

            <Title order={4} weight={500}>
              Medical Information
            </Title>

            <TextInput
              data-cy='eContactInput'
              label='Emergency Contact'
              placeholder='Enter Full Name'
              icon={<IconTags />}
              {...form.getInputProps('emergencyName')}
            />

            <Group grow align='top'>
              <Input.Wrapper error={form.getInputProps('emergencyPhone').error}>
                <Input.Label>Phone Number</Input.Label>
                <PhoneInput
                  defaultCountry={session?.gym?.country.toLowerCase()}
                  countries={phoneInputCountries}
                  value={form.values.emergencyPhone || ''}
                  onChange={form.setFieldValue.bind(null, 'emergencyPhone')}
                  placeholder='Enter Phone Number'
                  style={{ position: 'relative', zIndex: 2 }}
                  inputStyle={{ width: '100%' }}
                />
              </Input.Wrapper>
            </Group>

            <Textarea
              data-cy='medicalNotesInput'
              label='Medical Notes'
              placeholder='Enter Medical Notes'
              icon={<IconFileDescription />}
              {...form.getInputProps('medicalNotes')}
            />

            {type === 'edit' && gymData?.data?.data.isSmsEnabled && (
              <Checkbox
                mt='xl'
                label='Disable SMS Notifications'
                checked={form.values.disableSmsNotifications}
                {...form.getInputProps('disableSmsNotifications')}
              />
            )}
          </Stack>
        </form>
      ) : (
        <form id={'independent-to-linked'} onSubmit={e => handleLinkExistingAthlete(e)}>
          <Stack>
            <AthleteAutoComplete
              label='Choose an existing athlete'
              onChange={val =>
                val ? setExistingAthlete(val) : setExistingAthlete({ athleteId: '', label: '', value: '' })
              }
              value={existingAthlete}
              data-cy='chooseExistingAthlete'
            />
            <Switch
              label='Or click here to add new'
              onClick={() => setIsExistingAthlete(!isExistingAthlete)}
            />
          </Stack>
        </form>
      )}
    </Drawer>
  );
};

export default DrawerAthleteForm;
